/**
 * Independent before/after comparison for bulk audit fixes (authoring#1948).
 * Works on plain JSON and knows nothing about the fix that produced the change, so it can catch a fix
 * doing more than it should (French wiped, voice-overs or elements deleted, edits outside the fix's scope).
 */

export type JsonPath = (string | number)[];

export enum EDiffKind {
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
  CHANGED = 'CHANGED',
}

export interface IJsonDiffEntry {
  path: string;
  kind: EDiffKind;
  before?: any;
  after?: any;
}

export interface IJsonDiff {
  entries: IJsonDiffEntry[];
  /** leaves (values that are not objects or arrays) */
  added: number;
  removed: number;
  changed: number;
  truncated: boolean;
}

export enum EBulkFixRisk {
  FRENCH_REMOVED = 'FRENCH_REMOVED',
  FRENCH_TEXT_REDUCED = 'FRENCH_TEXT_REDUCED',
  TEXT_REDUCED = 'TEXT_REDUCED',
  VOICEOVER_REMOVED = 'VOICEOVER_REMOVED',
  IMAGE_REMOVED = 'IMAGE_REMOVED',
  ELEMENTS_REMOVED = 'ELEMENTS_REMOVED',
  LARGE_DELETION = 'LARGE_DELETION',
  OUTSIDE_FIX_SCOPE = 'OUTSIDE_FIX_SCOPE',
}

export const BULK_FIX_RISK_CAPTIONS: { [code in EBulkFixRisk]: string } = {
  FRENCH_REMOVED: 'French content removed',
  FRENCH_TEXT_REDUCED: 'French text reduced',
  TEXT_REDUCED: 'Text reduced',
  VOICEOVER_REMOVED: 'Voice-over removed',
  IMAGE_REMOVED: 'Image removed',
  ELEMENTS_REMOVED: 'Content elements removed',
  LARGE_DELETION: 'Large deletion',
  OUTSIDE_FIX_SCOPE: 'Changes outside the fix',
};

export interface IBulkFixRiskFlag {
  code: EBulkFixRisk;
  detail: string;
}

export interface IBulkFixItemReport {
  questionId: number;
  label: string;
  isChanged: boolean;
  added: number;
  removed: number;
  changed: number;
  flags: IBulkFixRiskFlag[];
  /** first changed paths, for the summary */
  samplePaths: string[];
}

export interface IBulkFixPreview {
  items: IBulkFixItemReport[];
  totals: {
    items: number;
    changed: number;
    unchanged: number;
    flagged: number;
    added: number;
    removed: number;
    changedLeaves: number;
  };
  flagCounts: { [code: string]: number };
}

export interface IBulkFixRiskOptions {
  /** a change is expected only where some path segment matches (e.g. /^scoreMatrix$/) */
  expectedPathPattern?: RegExp;
  /** removed leaves above this count are flagged (default 25) */
  largeDeletionThreshold?: number;
  /** share of non-empty text that may disappear before it is flagged (default 0.2) */
  textReductionRatio?: number;
}

const MAX_ENTRIES = 500;
const SAMPLE_PATHS = 8;
const VOICE_KEY = /voice/i;
const IMAGE_VALUE = /\.(png|jpe?g|gif|svg|webp)(\?|$)/i;

const isObject = (v: any) => v !== null && typeof v === 'object';
const formatPath = (path: JsonPath) => path.length
  ? path.map(seg => typeof seg === 'number' ? `[${seg}]` : `.${seg}`).join('').replace(/^\./, '')
  : '(root)';

/** Deep copy through JSON, which is also what is saved, so functions and undefined are dropped the same way. */
export const toPlainJson = <T>(value: T): T => value === undefined ? value : JSON.parse(JSON.stringify(value));

const countLeaves = (value: any): number => {
  if (Array.isArray(value)) { return value.reduce((n, v) => n + countLeaves(v), 0); }
  if (isObject(value)) { return Object.keys(value).reduce((n, k) => n + countLeaves(value[k]), 0); }
  return value === undefined ? 0 : 1;
};

/**
 * Structural diff: objects by key, arrays by index. A removed or added subtree counts each of its leaves,
 * so deleting a whole element shows up with its real size.
 */
export const diffJson = (before: any, after: any): IJsonDiff => {
  const diff: IJsonDiff = { entries: [], added: 0, removed: 0, changed: 0, truncated: false };
  const push = (entry: IJsonDiffEntry) => {
    if (diff.entries.length < MAX_ENTRIES) { diff.entries.push(entry); }
    else { diff.truncated = true; }
  };
  const walk = (b: any, a: any, path: JsonPath) => {
    if (b === undefined && a === undefined) { return; }
    if (b === undefined) {
      diff.added += Math.max(1, countLeaves(a));
      push({ path: formatPath(path), kind: EDiffKind.ADDED, after: a });
      return;
    }
    if (a === undefined) {
      diff.removed += Math.max(1, countLeaves(b));
      push({ path: formatPath(path), kind: EDiffKind.REMOVED, before: b });
      return;
    }
    if (Array.isArray(b) && Array.isArray(a)) {
      const n = Math.max(b.length, a.length);
      for (let i = 0; i < n; i++) { walk(b[i], a[i], [...path, i]); }
      return;
    }
    if (isObject(b) && isObject(a) && !Array.isArray(b) && !Array.isArray(a)) {
      const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
      keys.forEach(key => walk(b[key], a[key], [...path, key]));
      return;
    }
    if (isObject(b) || isObject(a)) {
      // shape changed (e.g. object replaced by a string): count it as removal plus addition
      diff.removed += Math.max(1, countLeaves(b));
      diff.added += Math.max(1, countLeaves(a));
      push({ path: formatPath(path), kind: EDiffKind.CHANGED, before: b, after: a });
      return;
    }
    if (b !== a) {
      diff.changed++;
      push({ path: formatPath(path), kind: EDiffKind.CHANGED, before: b, after: a });
    }
  };
  walk(before, after, []);
  return diff;
};

/** keys whose strings are not readable text (types, links, ids); voice-over subtrees are counted separately */
const NON_TEXT_KEYS = new Set(['elementType', 'url', 'entryId', 'id', 'label', 'type']);
const URL_VALUE = /^(https?:)?\/\//i;

const collectText = (value: any, skipKeys: Set<string> = new Set(), key = ''): number => {
  if (typeof value === 'string') {
    const text = value.trim();
    return text && !URL_VALUE.test(text) && !NON_TEXT_KEYS.has(key) ? text.length : 0;
  }
  if (Array.isArray(value)) { return value.reduce((n, v) => n + collectText(v, skipKeys, key), 0); }
  if (isObject(value)) {
    return Object.keys(value).reduce((n, k) => skipKeys.has(k) || VOICE_KEY.test(k) ? n : n + collectText(value[k], skipKeys, k), 0);
  }
  return 0;
};

const isFilled = (v: any) => v !== null && v !== undefined && v !== '' && v !== false;

/** filled values anywhere under a key that names a voice-over (`voiceover.url`, `voiceOverSlug`, ...) */
const countVoiceValues = (value: any, inVoice = false): number => {
  if (Array.isArray(value)) { return value.reduce((n, v) => n + countVoiceValues(v, inVoice), 0); }
  if (isObject(value)) {
    return Object.keys(value).reduce((n, k) => n + countVoiceValues(value[k], inVoice || VOICE_KEY.test(k)), 0);
  }
  return inVoice && isFilled(value) ? 1 : 0;
};

const countImageValues = (value: any): number => {
  if (Array.isArray(value)) { return value.reduce((n, v) => n + countImageValues(v), 0); }
  if (isObject(value)) { return Object.keys(value).reduce((n, k) => n + countImageValues(value[k]), 0); }
  return typeof value === 'string' && IMAGE_VALUE.test(value) ? 1 : 0;
};

const countContentElements = (value: any): number => {
  let n = 0;
  const walk = (v: any, key: string) => {
    if (Array.isArray(v)) {
      if (key === 'content') { n += v.filter(isObject).length; }
      v.forEach(x => walk(x, key));
    } else if (isObject(v)) {
      Object.keys(v).forEach(k => walk(v[k], k));
    }
  };
  walk(value, '');
  return n;
};

const pathSegments = (path: string) => path.split(/[.[\]]+/).filter(Boolean);

/** Risk flags for one item, from the item JSON before and after the fix. */
export const assessBulkFixRisk = (before: any, after: any, diff: IJsonDiff, opts: IBulkFixRiskOptions = {}): IBulkFixRiskFlag[] => {
  const flags: IBulkFixRiskFlag[] = [];
  const largeDeletion = opts.largeDeletionThreshold ?? 25;
  const ratio = opts.textReductionRatio ?? 0.2;

  const frBefore = collectText(before?.langLink);
  const frAfter = collectText(after?.langLink);
  if (frBefore > 0 && frAfter === 0) {
    flags.push({ code: EBulkFixRisk.FRENCH_REMOVED, detail: `French text went from ${frBefore} characters to none` });
  } else if (frBefore > 0 && (frBefore - frAfter) / frBefore > ratio) {
    flags.push({ code: EBulkFixRisk.FRENCH_TEXT_REDUCED, detail: `French text went from ${frBefore} to ${frAfter} characters` });
  }

  const skip = new Set(['langLink']);
  const enBefore = collectText(before, skip);
  const enAfter = collectText(after, skip);
  if (enBefore > 0 && (enBefore - enAfter) / enBefore > ratio) {
    flags.push({ code: EBulkFixRisk.TEXT_REDUCED, detail: `Text went from ${enBefore} to ${enAfter} characters` });
  }

  const voiceBefore = countVoiceValues(before);
  const voiceAfter = countVoiceValues(after);
  if (voiceAfter < voiceBefore) {
    flags.push({ code: EBulkFixRisk.VOICEOVER_REMOVED, detail: `Voice-over values went from ${voiceBefore} to ${voiceAfter}` });
  }

  const imagesBefore = countImageValues(before);
  const imagesAfter = countImageValues(after);
  if (imagesAfter < imagesBefore) {
    flags.push({ code: EBulkFixRisk.IMAGE_REMOVED, detail: `Image links went from ${imagesBefore} to ${imagesAfter}` });
  }

  const elementsBefore = countContentElements(before);
  const elementsAfter = countContentElements(after);
  if (elementsAfter < elementsBefore) {
    flags.push({ code: EBulkFixRisk.ELEMENTS_REMOVED, detail: `Content elements went from ${elementsBefore} to ${elementsAfter}` });
  }

  if (diff.removed > largeDeletion) {
    flags.push({ code: EBulkFixRisk.LARGE_DELETION, detail: `${diff.removed} values removed` });
  }

  if (opts.expectedPathPattern) {
    const outside = diff.entries.filter(e => !pathSegments(e.path).some(seg => opts.expectedPathPattern.test(seg)));
    if (outside.length) {
      const more = outside.length > 3 ? ` (+${outside.length - 3} more)` : '';
      flags.push({
        code: EBulkFixRisk.OUTSIDE_FIX_SCOPE,
        detail: `${outside.length} change(s) outside the fix: ${outside.slice(0, 3).map(e => e.path).join(', ')}${more}`,
      });
    }
  }
  return flags;
};

export const reportItemChange = (
  question: { id?: number, label?: string },
  before: any,
  after: any,
  opts: IBulkFixRiskOptions = {},
): IBulkFixItemReport => {
  const diff = diffJson(before, after);
  const isChanged = diff.entries.length > 0;
  return {
    questionId: question?.id,
    label: question?.label,
    isChanged,
    added: diff.added,
    removed: diff.removed,
    changed: diff.changed,
    flags: isChanged ? assessBulkFixRisk(before, after, diff, opts) : [],
    samplePaths: diff.entries.slice(0, SAMPLE_PATHS).map(e => `${e.kind.toLowerCase()} ${e.path}`),
  };
};

/**
 * Dry run: applies the fix to a JSON copy of each item (never the item itself) and reports the difference.
 */
export const buildBulkFixPreview = <Q extends { id?: number, label?: string }>(
  questions: Q[],
  applyToCopy: (copy: Q) => void,
  opts: IBulkFixRiskOptions = {},
): IBulkFixPreview => {
  const items = (questions ?? []).filter(Boolean).map(question => {
    const before = toPlainJson(question);
    const copy = toPlainJson(question);
    try {
      applyToCopy(copy);
    } catch (e) {
      return { ...reportItemChange(question, before, before, opts), samplePaths: [`fix failed: ${e?.message ?? e}`] };
    }
    return reportItemChange(question, before, toPlainJson(copy), opts);
  });
  const flagCounts: { [code: string]: number } = {};
  items.forEach(item => item.flags.forEach(f => flagCounts[f.code] = (flagCounts[f.code] ?? 0) + 1));
  const changed = items.filter(i => i.isChanged);
  return {
    items,
    totals: {
      items: items.length,
      changed: changed.length,
      unchanged: items.length - changed.length,
      flagged: items.filter(i => i.flags.length).length,
      added: changed.reduce((n, i) => n + i.added, 0),
      removed: changed.reduce((n, i) => n + i.removed, 0),
      changedLeaves: changed.reduce((n, i) => n + i.changed, 0),
    },
    flagCounts,
  };
};

/** True when the live change raises a risk the author did not see in the preview. */
export const hasUnapprovedRisk = (previewed: IBulkFixItemReport | undefined, live: IBulkFixItemReport): boolean => {
  const approved = new Set((previewed?.flags ?? []).map(f => f.code));
  return live.flags.some(f => !approved.has(f.code));
};

/** Puts an item object back to a snapshot taken with `toPlainJson`, keeping the same object reference. */
export const restoreQuestionFromSnapshot = <Q extends object>(question: Q, snapshot: Q) => {
  Object.keys(question).forEach(key => delete question[key]);
  Object.assign(question, toPlainJson(snapshot));
};

export interface IBulkFixSuspenseOption {
  id: string;
  caption: string;
  value: boolean;
  isDisabled?: boolean;
}

/** Dry-run state shown in the bulk fix modal before anything is saved. */
export interface IBulkFixSuspense {
  title: string;
  intro: string[];
  options: IBulkFixSuspenseOption[];
  preview: IBulkFixPreview | null;
  isComputing: boolean;
  showUnchanged: boolean;
  /** items the author unticked in the preview; they are left out of Apply (by question id) */
  /** question ids processed even when unchanged (their expected answers are re-validated) */
  processedIds?: number[];
  excludedIds?: { [questionId: number]: boolean };
  recompute: () => void;
  apply: () => void;
  cancel: () => void;
  /** preview -> running (saving in the background, progress shown) -> done (outcome shown); authoring#1959 */
  phase?: 'preview' | 'running' | 'done';
  progress?: { done: number, total: number };
  outcome?: IBulkFixOutcome | null;
  /** items whose options would still need an author's review after the fix, known before applying */
  optionIssues?: { label: string, reasons: string[] }[];
}

export interface IBulkFixOutcome {
  saved: { label: string }[];
  blocked: { label: string, reason: string }[];
  skipped: { label: string, reason: string }[];
  /** saved items whose options still need an author's attention after the fix (authoring#1959) */
  review?: { label: string, reason: string }[];
  /** saved items whose simulated responses were validated against the saved score matrix */
  validated?: { label: string }[];
  /** saved items whose simulated responses could NOT be validated against the matrix */
  unvalidated?: { label: string, reason: string }[];
}
